import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, Lock, Unlock, KeyRound, AlertTriangle, History,
  Search, Copy, Check, User as UserIcon, UserCheck, UserX, UserCog
} from "lucide-react";

const ACTION_META = {
  password_change:     { color: "#059669", label: "Password Changed",    icon: KeyRound },
  password_reset:      { color: "#D97706", label: "Password Reset",      icon: KeyRound },
  failed_login:        { color: "#DC2626", label: "Failed Login",        icon: AlertTriangle },
  account_locked:      { color: "#DC2626", label: "Account Locked",      icon: Lock },
  account_unlocked:    { color: "#059669", label: "Account Unlocked",    icon: Unlock },
  account_activated:   { color: "#059669", label: "Account Activated",   icon: UserCheck },
  account_deactivated: { color: "#DC2626", label: "Account Deactivated", icon: UserX },
  role_changed:        { color: "#0F2E4A", label: "Role Changed",        icon: UserCog },
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [resetDialog, setResetDialog] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [roleDialog, setRoleDialog] = useState(null);
  const [roleForm, setRoleForm] = useState({ role: "student", assigned_batch: "" });
  const [copied, setCopied] = useState(false);

  const loadUsers = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  const loadAudit = async () => {
    const params = actionFilter !== "all" ? { action: actionFilter } : {};
    const { data } = await api.get("/admin/audit-log", { params });
    setAudit(data);
  };
  const loadBatches = async () => {
    try {
      const { data } = await api.get("/batches");
      setBatches(data);
    } catch (e) { /* non-admin fallback */ }
  };

  useEffect(() => { if (isAdmin) { loadUsers(); loadAudit(); loadBatches(); } /* eslint-disable-next-line */ }, [isAdmin]);
  useEffect(() => { if (isAdmin) loadAudit(); /* eslint-disable-next-line */ }, [actionFilter]);

  const onReset = async (user) => {
    try {
      const { data } = await api.post(`/admin/users/${user.id}/reset-password`);
      setResetResult({ user, temp: data.temp_password });
      setResetDialog(null);
      await loadUsers();
      await loadAudit();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const onUnlock = async (user) => {
    try {
      await api.post(`/admin/users/${user.id}/unlock`);
      toast.success(`Unlocked ${user.name}`);
      await loadUsers();
      await loadAudit();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const onToggleActive = async (user) => {
    const endpoint = user.active === false ? "activate" : "deactivate";
    if (endpoint === "deactivate" && !window.confirm(`Deactivate ${user.name}? They will not be able to login until reactivated.`)) return;
    try {
      await api.post(`/admin/users/${user.id}/${endpoint}`);
      toast.success(`${endpoint === "activate" ? "Activated" : "Deactivated"} ${user.name}`);
      await loadUsers();
      await loadAudit();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const openRoleDialog = (u) => {
    setRoleForm({ role: u.role, assigned_batch: u.assigned_batch || "" });
    setRoleDialog(u);
  };

  const onSaveRole = async () => {
    try {
      await api.put(`/admin/users/${roleDialog.id}/role`, {
        role: roleForm.role,
        assigned_batch: roleForm.role === "team_lead" ? (roleForm.assigned_batch || null) : null,
      });
      toast.success(`Role updated to ${roleForm.role}`);
      setRoleDialog(null);
      await loadUsers();
      await loadAudit();
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const copyTemp = async () => {
    try {
      await navigator.clipboard.writeText(resetResult.temp);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Clipboard copy failed", e);
      toast.error("Could not copy — please select the password manually");
    }
  };

  const filtered = users.filter((u) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (u.name || "").toLowerCase().includes(f)
        || (u.email || "").toLowerCase().includes(f)
        || (u.student_id || "").toLowerCase().includes(f);
  });

  const stats = {
    total: users.length,
    locked: users.filter((u) => u.locked).length,
    must_change: users.filter((u) => u.must_change_password).length,
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-1">Security</div>
          <h1 className="font-heading text-3xl font-bold text-[#0F172A]">User Management</h1>
          <p className="text-sm text-[#475569] mt-1">Reset passwords, unlock accounts, and review the security audit trail.</p>
        </div>
        <div className="flex gap-3 text-xs">
          <Stat label="Accounts" value={stats.total} color="#0F2E4A" />
          <Stat label="Locked" value={stats.locked} color="#DC2626" />
          <Stat label="Must Change Pwd" value={stats.must_change} color="#D97706" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users"><UserIcon className="w-3.5 h-3.5 mr-1.5" /> Users</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><History className="w-3.5 h-3.5 mr-1.5" /> Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6 space-y-4">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <Input
              data-testid="users-search"
              placeholder="Search by name, email, ID..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Failed</th>
                  <th className="px-4 py-3">Last Login</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#64748B]">No users.</td></tr>}
                {filtered.map((u) => {
                  const locked = !!u.locked;
                  const mustChange = !!u.must_change_password;
                  const inactive = u.active === false;
                  return (
                    <tr key={u.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]">
                      <td className="px-4 py-3 font-medium text-[#0F172A]">
                        {u.name}
                        {u.assigned_batch && <div className="text-[10px] text-[#64748B] font-normal mt-0.5">Team: {u.assigned_batch}</div>}
                      </td>
                      <td className="px-4 py-3 text-[#475569]">{u.email}</td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-[#0F2E4A]/10 text-[#0F2E4A]">
                          {u.role === "team_lead" ? "Team Lead" : u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {inactive && <Pill color="#64748B" icon={UserX}>Deactivated</Pill>}
                          {locked && <Pill color="#DC2626" icon={Lock}>Locked</Pill>}
                          {mustChange && <Pill color="#D97706" icon={KeyRound}>Must change password</Pill>}
                          {!inactive && !locked && !mustChange && <Pill color="#059669" icon={ShieldCheck}>Active</Pill>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono-plex">
                        <span className={u.failed_attempts > 0 ? "text-[#DC2626] font-semibold" : "text-[#64748B]"}>
                          {u.failed_attempts || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#64748B]">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap space-x-1">
                        {locked && (
                          <Button size="sm" variant="outline" onClick={() => onUnlock(u)} data-testid={`unlock-${u.id}`} className="border-[#059669]/30 text-[#059669] hover:bg-[#ECFDF5]">
                            <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => setResetDialog(u)} data-testid={`reset-${u.id}`} className="border-[#D97706]/30 text-[#D97706] hover:bg-[#FFFBEB]">
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openRoleDialog(u)} data-testid={`role-${u.id}`} className="border-[#0F2E4A]/30 text-[#0F2E4A] hover:bg-[#F1F5F9]">
                          <UserCog className="w-3.5 h-3.5 mr-1" /> Role
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onToggleActive(u)} data-testid={`toggle-active-${u.id}`}
                                className={inactive ? "border-[#059669]/30 text-[#059669] hover:bg-[#ECFDF5]" : "border-[#DC2626]/30 text-[#DC2626] hover:bg-[#FEF2F2]"}>
                          {inactive ? <><UserCheck className="w-3.5 h-3.5 mr-1" /> Activate</> : <><UserX className="w-3.5 h-3.5 mr-1" /> Deactivate</>}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-6 space-y-4">
          <div className="flex items-center gap-3">
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-64" data-testid="audit-action-filter">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="password_change">Password Changes</SelectItem>
                <SelectItem value="password_reset">Password Resets</SelectItem>
                <SelectItem value="failed_login">Failed Logins</SelectItem>
                <SelectItem value="account_locked">Account Lockouts</SelectItem>
                <SelectItem value="account_unlocked">Account Unlocks</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-[#64748B]">{audit.length} entries</div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC] text-left text-[11px] font-semibold uppercase tracking-wider text-[#475569]">
                <tr>
                  <th className="px-4 py-3">Timestamp</th>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Performed By</th>
                  <th className="px-4 py-3">Affected User</th>
                  <th className="px-4 py-3">Details</th>
                </tr>
              </thead>
              <tbody data-testid="audit-log-table">
                {audit.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#64748B]">No audit entries yet.</td></tr>}
                {audit.map((e) => {
                  const meta = ACTION_META[e.action] || { color: "#0F2E4A", label: e.action, icon: History };
                  const Icon = meta.icon;
                  return (
                    <tr key={e.id} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-3 text-xs font-mono-plex text-[#475569]">{new Date(e.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded" style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {e.actor_email ? (
                          <>
                            <div className="font-medium text-[#0F172A]">{e.actor_name || e.actor_email}</div>
                            <div className="text-[10px] text-[#64748B]">{e.actor_role}</div>
                          </>
                        ) : (
                          <span className="text-[#94A3B8] italic">system</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {e.target_email ? (
                          <>
                            <div className="font-medium text-[#0F172A]">{e.target_name || "—"}</div>
                            <div className="text-[10px] text-[#64748B]">{e.target_email}</div>
                          </>
                        ) : <span className="text-[#94A3B8]">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#475569] max-w-[280px] truncate">
                        {e.details && Object.keys(e.details).length > 0 ? JSON.stringify(e.details) : <span className="text-[#94A3B8]">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Role change dialog */}
      <Dialog open={!!roleDialog} onOpenChange={(v) => { if (!v) setRoleDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign role for {roleDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-[#475569]">Role</Label>
              <Select value={roleForm.role} onValueChange={(v) => setRoleForm({ ...roleForm, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — full system access</SelectItem>
                  <SelectItem value="manager">Manager — approvals & reports</SelectItem>
                  <SelectItem value="team_lead">Team Lead — one batch</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {roleForm.role === "team_lead" && (
              <div>
                <Label className="text-xs font-semibold text-[#475569]">Assigned Batch *</Label>
                <Select value={roleForm.assigned_batch} onValueChange={(v) => setRoleForm({ ...roleForm, assigned_batch: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="role-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                  <SelectContent>
                    {batches.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(null)}>Cancel</Button>
            <Button
              onClick={onSaveRole}
              data-testid="save-role"
              disabled={roleForm.role === "team_lead" && !roleForm.assigned_batch}
              className="bg-[#0F2E4A] hover:bg-[#174169] text-white"
            >
              <UserCog className="w-4 h-4 mr-1.5" /> Save Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset confirmation */}
      <Dialog open={!!resetDialog} onOpenChange={(v) => { if (!v) setResetDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password for {resetDialog?.name}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#475569] space-y-3">
            <p>This will:</p>
            <ul className="list-disc list-inside space-y-1 text-xs text-[#475569]">
              <li>Generate a strong temporary password</li>
              <li>Force <span className="font-semibold text-[#0F172A]">{resetDialog?.email}</span> to change their password on next login</li>
              <li>Unlock the account if it was locked</li>
              <li>Record this action in the audit log</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(null)}>Cancel</Button>
            <Button onClick={() => onReset(resetDialog)} data-testid="confirm-reset" className="bg-[#D97706] hover:bg-[#B45309] text-white">
              <KeyRound className="w-4 h-4 mr-1.5" /> Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset success — show temp password */}
      <Dialog open={!!resetResult} onOpenChange={(v) => { if (!v) setResetResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary password generated</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#475569]">
              Share this with <span className="font-semibold text-[#0F172A]">{resetResult?.user?.name}</span> ({resetResult?.user?.email}).
              They will be required to change it on their next login.
            </p>
            <div className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-3">
              <code className="flex-1 font-mono-plex text-lg font-semibold text-[#0F172A] tracking-wider select-all" data-testid="temp-password-display">
                {resetResult?.temp}
              </code>
              <Button size="sm" variant="outline" onClick={copyTemp} data-testid="copy-temp-password">
                {copied ? <Check className="w-3.5 h-3.5 text-[#059669]" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <p className="text-[11px] text-[#D97706]">⚠ This password will not be shown again. Copy it now.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setResetResult(null)} className="bg-[#0F2E4A] hover:bg-[#174169] text-white">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md px-4 py-2.5 min-w-[110px]">
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#64748B] font-semibold">{label}</div>
      <div className="font-heading text-xl font-bold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function Pill({ color, icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded w-fit" style={{ backgroundColor: `${color}15`, color }}>
      <Icon className="w-3 h-3" /> {children}
    </span>
  );
}
